import { useMemo } from 'react';

import { resolveBrandingLogoUrl } from '../../utils/brandingUrl';
import './publicPartnersStrip.css';

type PartnerItem = {
  id: string;
  nom: string;
  urlLogo: string;
  urlSite?: string | null;
  categorie: 'PARTENAIRE' | 'SPONSOR';
};

type Props = {
  partenaires: PartnerItem[];
  sponsors: PartnerItem[];
  loading?: boolean;
};

function splitPartnerRows(items: PartnerItem[]): [PartnerItem[], PartnerItem[]] {
  const row1: PartnerItem[] = [];
  const row2: PartnerItem[] = [];

  items.forEach((item, index) => {
    if (index % 2 === 0) {
      row1.push(item);
    } else {
      row2.push(item);
    }
  });

  if (row2.length === 0 && row1.length > 0) {
    row2.push(...row1);
  }

  return [row1, row2];
}

/** Deux copies pour boucle CSS à -50 % sans couture. */
function buildMarqueeTrack(items: PartnerItem[]): PartnerItem[] {
  if (items.length === 0) return [];

  const minSets = Math.max(2, Math.ceil(12 / items.length));
  const oneLoop = Array.from({ length: minSets }, () => items).flat();

  return [...oneLoop, ...oneLoop];
}

function PartnerLogo({ item }: { item: PartnerItem }) {
  const logoSrc = resolveBrandingLogoUrl(item.urlLogo);
  const inner = (
    <span className="public-partners-strip__logo-card">
      {logoSrc ? (
        <img src={logoSrc} alt="" loading="lazy" decoding="async" />
      ) : (
        <span className="public-partners-strip__name-fallback">{item.nom}</span>
      )}
    </span>
  );

  const tooltip = (
    <span className="public-partners-strip__tooltip" role="tooltip">
      {item.nom}
      {item.urlSite ? (
        <span className="public-partners-strip__tooltip-hint">Visiter le site</span>
      ) : null}
    </span>
  );

  if (item.urlSite) {
    return (
      <a
        href={item.urlSite}
        target="_blank"
        rel="noopener noreferrer"
        className="public-partners-strip__logo-link"
        aria-label={`${item.nom} (nouvelle fenêtre)`}
      >
        {tooltip}
        {inner}
      </a>
    );
  }

  return (
    <div className="public-partners-strip__logo-link" aria-label={item.nom}>
      {tooltip}
      {inner}
    </div>
  );
}

type MarqueeRowProps = {
  items: PartnerItem[];
  direction: 'rtl' | 'ltr';
  label: string;
};

function PartnersMarqueeRow({ items, direction, label }: MarqueeRowProps) {
  const trackItems = useMemo(() => buildMarqueeTrack(items), [items]);
  const half = trackItems.length / 2;

  if (trackItems.length === 0) return null;

  return (
    <div className="public-partners-strip__marquee-viewport" role="region" aria-label={label}>
      <div
        className={`public-partners-strip__marquee-track public-partners-strip__marquee-track--${direction}`}
      >
        {trackItems.map((item, index) => (
          <div
            key={`${item.id}-${index}`}
            className="public-partners-strip__marquee-item"
            role="listitem"
            aria-hidden={index >= half ? true : undefined}
          >
            <PartnerLogo item={item} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PublicPartnersLogoStrip({ partenaires, sponsors, loading = false }: Props) {
  if (loading) return null;

  const items = [...partenaires, ...sponsors];
  if (items.length === 0) return null;

  const [row1, row2] = splitPartnerRows(items);

  return (
    <section
      id="partenaires"
      className="public-partners-strip scroll-mt-20"
      aria-labelledby="public-partners-heading"
    >
      <div className="public-partners-strip__inner">
        <h2 id="public-partners-heading" className="public-partners-strip__title">
          Nos partenaires
        </h2>
      </div>

      <div className="public-partners-strip__rows" role="list">
        <PartnersMarqueeRow items={row1} direction="rtl" label="Partenaires — ligne 1" />
        <PartnersMarqueeRow items={row2} direction="ltr" label="Partenaires — ligne 2" />
      </div>
    </section>
  );
}
